import { computed, inject, nextTick, onScopeDispose, shallowRef, watch } from "vue";
import MarkerClusterer from "@googlemaps/markerclustererplus";
import { useHostElement } from "#ce/composables/useHostElement";
import { useFinderMapScene } from "#ce/apps/SpFinderMap/composables/useFinderMapScene";
import { useFinderMapViewport } from "#ce/apps/SpFinderMap/composables/useFinderMapViewport";
import { useFinderMapLayout } from "#ce/apps/SpFinderMap/composables/useFinderMapLayout";
import { useFinderMapPins } from "#ce/apps/SpFinderMap/composables/useFinderMapPins";
import { useFinderMapData } from "#ce/apps/SpFinderMap/composables/useFinderMapData";
import { parseMapBounds } from "#ce/apps/SpFinderMap/lib/parse-map-bounds";
import { fitMapBounds } from "#ce/apps/SpFinderMap/lib/map-fit-bounds";
import { createMapPinsApi } from "#ce/apps/SpFinderMap/api/map-pins";

/**
 * Runs the desktop map for a finder's completed results: the scene, its camera, coordinate pins,
 * the bounds viewport and the sticky layout. `canvas` and `sidebar` are the surface's template refs;
 * map events from `sp-google-map` arrive through the returned handlers.
 */
export function useFinderMapSurface(props, { canvas, sidebar }) {
  const host = useHostElement();
  const map = shallowRef();
  const maps = shallowRef();
  const error = shallowRef();
  const content = shallowRef();
  const infoTarget = document.createElement("div");
  // No starting camera: the map stays blank until results or the default view frame it.
  const options = computed(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    zoomControlOptions: { position: maps.value?.ControlPosition?.TOP_RIGHT },
    scrollwheel: false,
  }));
  function renderInfo(value) {
    content.value = value;
    return infoTarget;
  }
  function close() {
    props.commands.clearMapSelection();
  }
  const { scene } = useFinderMapScene({
    map,
    maps,
    records: () => props.records,
    icons: () => props.settings?.icons ?? {},
    renderInfo,
    onClose: close,
    Clusterer: inject("finderMapClusterer", MarkerClusterer),
    clusterMarkers: () => props.settings?.clusterMarkers,
  });
  const { offset, headerOffset } = useFinderMapLayout({
    canvas,
    sidebar,
    fallback: () => props.settings?.stickyOffset,
  });
  const layoutStyle = computed(() => ({
    position: "relative",
    "--finder-map-offset": `${offset.value}px`,
    "--finder-map-header-offset": `${headerOffset.value}px`,
  }));
  const api = computed(() =>
    props.settings?.coordinatesUrl && props.settings?.pinInfoUrl ? createMapPinsApi(props.settings) : undefined,
  );
  // Coordinate pins follow the last search with results, so an empty one neither loads nor clears them.
  const pinParams = shallowRef();
  function trackPinParams() {
    if (props.records?.length) {
      pinParams.value = props.params;
    }
  }
  watch([() => props.records, () => props.params], trackPinParams, { immediate: true });
  const pins = useFinderMapPins({ api, params: pinParams, generation: () => props.generation });
  useFinderMapData({
    map,
    maps,
    scene,
    feed: pins.coordinates,
    icon: () => props.settings?.icons?.micro,
    selectPin: pins.selectPin,
  });
  function readMapBounds() {
    return map.value?.getBounds?.()?.toJSON?.();
  }
  function applyBounds(bounds) {
    props.commands.applyMapBounds(bounds);
  }
  function setBoundsMode(mode) {
    props.commands.setMapBoundsMode(mode);
  }
  const viewport = useFinderMapViewport({
    mode: () => props.mode,
    initialAuto: () => props.settings?.autoBounds,
    enabled: () => props.settings?.boundsScopingEnabled,
    applyBounds,
    setMode: setBoundsMode,
    readBounds: readMapBounds,
  });
  function suppressMove() {
    viewport.programmatic();
  }
  function viewportChanged({ detail: [value] }) {
    viewport.changed(value);
  }
  function appliedBounds({ north, south, east, west }) {
    return { north: Number(north), south: Number(south), east: Number(east), west: Number(west) };
  }
  function searchOrigin() {
    const { lat, lng } = props.payload ?? {};
    if (lat == null || lng == null || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
      return undefined;
    }
    return { lat: Number(lat), lng: Number(lng) };
  }
  async function scrollToCard(guid) {
    await nextTick();
    const cards = sidebar.value?.$el.querySelectorAll(".doctor-panel[data-result-guid]") ?? [];
    const card = [...cards].find((element) => element.dataset.resultGuid === guid);
    if (card) {
      window.scrollTo(0, card.getBoundingClientRect().top + window.scrollY - offset.value);
    }
  }
  function focusResult(guid) {
    const marker = scene.value.openOnIdle(guid);
    if (marker) {
      fitMapBounds({
        maps: maps.value,
        map: map.value,
        bounds: new maps.value.LatLngBounds().extend(marker.getPosition()),
        canvas: canvas.value?.getBoundingClientRect(),
        sidebar: sidebar.value?.$el.getBoundingClientRect(),
      });
    }
  }
  function restoreBounds(applied) {
    viewport.refitted();
    map.value.fitBounds(appliedBounds(applied), 0);
  }
  function showDefaultView() {
    const { defaultCenter, defaultZoom } = props.settings ?? {};
    if (defaultCenter && defaultZoom != null) {
      viewport.refitted();
      map.value.setOptions({ center: defaultCenter, zoom: defaultZoom });
    }
  }
  let framed = false;
  let announcedReady = false;
  function announceReady() {
    if (!announcedReady) {
      announcedReady = true;
      host?.dispatchEvent(new CustomEvent("doctor-map:ready", { bubbles: true }));
    }
  }
  function announceContent() {
    if (host?.isConnected) {
      host.dispatchEvent(new CustomEvent("sparkle:content-did-update", { bubbles: true }));
    }
  }
  // Viewport precedence: applied bounds, then a single result, then the selection, then every result, then the default view.
  function fit() {
    if (!scene.value) {
      return;
    }
    const applied = parseMapBounds(props.params?.bounds);
    if (!props.records?.length) {
      if (applied) {
        restoreBounds(applied);
      } else if (framed) {
        showDefaultView();
      }
      return;
    }
    frameResults(applied);
    framed = true;
    announceReady();
  }
  function frameResults(applied) {
    const selected = props.selection?.resultGUID;
    if (selected) {
      suppressMove();
      scene.value.select(selected);
    }
    if (applied) {
      restoreBounds(applied);
      return;
    }
    suppressMove();
    if (props.records.length === 1) {
      focusResult(props.records[0].resultGUID);
      return;
    }
    if (selected) {
      scrollToCard(selected);
      return;
    }
    const bounds = scene.value.bounds();
    const origin = searchOrigin();
    if (origin) {
      bounds.extend(origin);
    }
    fitMapBounds({
      maps: maps.value,
      map: map.value,
      bounds,
      canvas: canvas.value?.getBoundingClientRect(),
      sidebar: sidebar.value?.$el.getBoundingClientRect(),
    });
  }
  function ready({ detail: [value] }) {
    maps.value = value.maps;
    map.value = value.map;
    error.value = undefined;
  }
  function failed({ detail: [value] }) {
    error.value = value;
  }
  function disposed() {
    map.value = undefined;
    maps.value = undefined;
  }
  function seedAppliedBounds() {
    viewport.completed(props.params?.bounds);
  }
  function highlight(guid) {
    scene.value?.highlight(guid);
  }
  function unhighlight(guid) {
    scene.value?.unhighlight(guid);
  }
  function selected() {
    if (scene.value && props.selection?.resultGUID) {
      suppressMove();
    }
    scene.value?.select(props.selection?.resultGUID);
  }
  watch([scene, () => props.records, () => props.params?.bounds], fit, { flush: "post" });
  watch(() => props.selection, selected);
  watch(() => props.records, announceContent, { flush: "post", immediate: true });
  watch(() => props.generation, seedAppliedBounds, { immediate: true });
  onScopeDispose(() => {
    infoTarget.remove();
  });
  return {
    layoutStyle,
    options,
    viewport,
    error,
    content,
    infoTarget,
    viewportChanged,
    ready,
    failed,
    disposed,
    highlight,
    unhighlight,
  };
}
